import { Location, NgTemplateOutlet } from '@angular/common';
import { Component, computed, OnDestroy, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { MenuBannerComponent } from '@components/menu-banner/menu-banner.component';
import { ModalWrapperComponent } from '@components/modal-wrapper/modal-wrapper.component';
import { PopoverOptionsComponent } from '@components/popover-options/popover-options.component';
import { SidePanelWrapperComponent } from '@components/side-panel-wrapper/side-panel-wrapper.component';
import { ToastComponent } from '@components/toast/toast.component';
import { operoCompanyId } from '@constants/platform.constants';
import { time } from '@constants/time.constants';
import {
  IonApp,
  IonContent,
  IonMenu,
  IonSplitPane,
  LoadingController,
  MenuController,
  PopoverController,
  ToastController,
} from '@ionic/angular/standalone';
import { LocalCompany } from '@models/company.model';
import { LocalUser } from '@models/user.model';
import { AuthService, Session } from '@services/auth.service';
import { CompanyAdminActionsService } from '@services/company-admin-actions.service';
import { DataStorageKey, DataStorageService } from '@services/data-storage.service';
import { ErrorReportingService } from '@services/error-reporting.service';
import { ModalService } from '@services/modal.service';
import { RemoteConfigService } from '@services/remote-config.service';
import { SidePanelService } from '@services/side-panel.service';
import { ToastService } from '@services/toast.service';
import { PopoverOption } from '@utils/controls.utils';
import { filter, firstValueFrom, Subscription, timer } from 'rxjs';

interface DashboardRoute {
  icon: string;
  label: string;
  path: string;
}

const availableDashboardRoutes: Record<string, DashboardRoute> = {
  'clients': { icon: 'clients', label: 'Clientes', path: '/dashboard/clients' },
  'payments': { icon: 'dollar-sign', label: 'Pagos', path: '/dashboard/payments' },
  'quotes': { icon: 'file-text', label: 'Cotizaciones', path: '/dashboard/quotes' },
};

@Component({
  imports: [
    MenuBannerComponent,
    IonApp,
    IonContent,
    IonMenu,
    IonSplitPane,
    ModalWrapperComponent,
    SidePanelWrapperComponent,
    NgTemplateOutlet,
    RouterLink,
    RouterOutlet,
    ToastComponent,
  ],
  selector: 'app-root',
  styleUrls: ['app.component.scss'],
  templateUrl: 'app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
  readonly operoCompanyId = operoCompanyId;
  protected company = signal<LocalCompany | null>(null);
  protected currentDashboardModule = signal<string | null>(null);
  protected dashboardRoutes = signal<DashboardRoute[]>([]);
  protected isDashboardRoute = signal<boolean | null>(null);
  protected isDocumentPreviewRoute = signal<boolean | null>(null);
  protected isMaintenanceMode = signal<boolean>(false);
  protected menuSide = signal<'start' | 'end'>('start');
  protected minimize = signal(false);
  protected modals = this.modalService.get();
  protected sidePanels = this.sidePanelService.get();
  protected quoteTutorialCompleted = signal<boolean>(false);
  protected session = signal<Session | null>(null);
  protected user = signal<LocalUser | null>(null);
  protected userName = computed(() => `${this.user()?.name ?? ''} ${this.user()?.lastName ?? ''}`);
  private mediaQuery = window.matchMedia('(min-width: 992px)');
  private mediaQueryListener: ((e: MediaQueryListEvent) => void) | null = null;
  private routerEventsSubscription: Subscription | null = null;
  private skipMaintenanceMode = false;

  constructor(
    private authService: AuthService,
    private companyAdminActionsService: CompanyAdminActionsService,
    private dataStorageService: DataStorageService,
    private errorReportingService: ErrorReportingService,
    private loadingController: LoadingController,
    private location: Location,
    private menuController: MenuController,
    private modalService: ModalService,
    private sidePanelService: SidePanelService,
    private popoverController: PopoverController,
    private remoteConfigService: RemoteConfigService,
    private router: Router,
    private swUpdate: SwUpdate,
    private toastController: ToastController,
    private toastService: ToastService,
  ) {
    this.mediaQueryListener = (e) => this.onViewportChange(e.matches);
    this.mediaQuery.addEventListener('change', this.mediaQueryListener);
    this.menuSide.set(this.mediaQuery.matches ? 'start' : 'end');
    this.routerEventsSubscription = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe({
      error: (err) => this.errorReportingService.log('AppComponent.constructor()', 'router-events-subscription', err),
      next: (event) => {
        const dashboardPath = event.urlAfterRedirects.split('?')[0] ?? '';
        this.isDashboardRoute.set(dashboardPath.startsWith('/dashboard'));
        this.isDocumentPreviewRoute.set(dashboardPath.match(/^\/dashboard\/(?:quotes\/configuration-preview|(?:quotes|quote-templates)\/\d+\/(?:preview|send-preview))$/) !== null);
        this.currentDashboardModule.set(dashboardPath);
      },
    });
    this.initializeUpdater();
  }

  ngOnDestroy(): void {
    if (this.mediaQueryListener) {
      this.mediaQuery.removeEventListener('change', this.mediaQueryListener);
      this.mediaQueryListener = null;
    }
    this.routerEventsSubscription?.unsubscribe();
    this.routerEventsSubscription = null;
  }

  ngOnInit(): void {
    this.dataStorageService.get<LocalUser>(DataStorageKey.LocalUser).subscribe({
      error: (err) => this.errorReportingService.log('AppComponent.ngOnInit()', 'get-user', err),
      next: (localUser) => this.user.set(localUser),
    });
    this.dataStorageService.get<LocalCompany>(DataStorageKey.LocalCompany).subscribe({
      error: (err) => this.errorReportingService.log('AppComponent.ngOnInit()', 'get-company', err),
      next: (localCompany) => this.company.set(localCompany),
    });
    this.dataStorageService.get<boolean>(DataStorageKey.MenuMinimized).subscribe({
      next: (minimized) => {
        if (minimized !== null) {
          this.minimize.set(this.mediaQuery.matches ? minimized : false);
        }
      },
    });
    // TODO: uncomment when we have the tutorial and change the set value to the variable
    // this.dataStorageService.get<boolean>(DataStorageKey.QuoteTutorialCompleted).subscribe({
    //   error: (err) => this.errorReportingService.log('AppComponent.ngOnInit()', 'get-quote-tutorial-completed', err),
    //   next: (completed) => this.quoteTutorialCompleted.set(completed === true),
    // });
    this.quoteTutorialCompleted.set(true);
    this.authService.getSession().subscribe({
      error: (err) => this.errorReportingService.log('AppComponent.ngOnInit()', 'get-session', err),
      next: (session) => {
        this.session.set(session);
        this.setDashboardRoutes();
      },
    });
    this.remoteConfigService.get('maintenance-config').subscribe({
      error: (err) => this.errorReportingService.log('AppComponent.ngOnInit()', 'get-maintenance-config', err),
      next: async (remoteConfig) => {
        if (this.location.path().includes('skipMaintenanceMode=true')) {
          this.skipMaintenanceMode = true;
        }
        this.isMaintenanceMode.set(remoteConfig?.type === 'maintenance-config' && remoteConfig.value.enabled && !this.skipMaintenanceMode);
      },
    });
  }

  protected joinOperoCompany(): void {
    this.companyAdminActionsService.joinCompany({ id: operoCompanyId, name: 'Opero' });
  }

  protected minimizeMenu(): void {
    this.minimize.update((currentState) => !currentState);
    this.dataStorageService.set(DataStorageKey.MenuMinimized, this.minimize());
  }

  protected toggleMenu(): void {
    this.menuController.toggle('menu');
  }

  async openMenu(): Promise<void> {
    const options: PopoverOption<unknown>[][] = [];
    if (this.session()?.userCompanyRoles[this.company()?.id ?? 0] === 'admin') {
      options.push([{
        label: 'Mi empresa',
        onClick: () => this.router.navigateByUrl('/dashboard/company'),
      }]);
    }
    options.push([{
      label: 'Cerrar sesión',
      onClick: () => this.signOut(),
    }]);
    const popover = await this.popoverController.create({
      alignment: 'center',
      component: PopoverOptionsComponent,
      componentProps: {
        data: null,
        options,
      },
      cssClass: 'user-menu-popover',
      reference: 'trigger',
      side: 'top',
      trigger: 'click-mCR7KSKa',
    });
    await popover.present();
  }

  async signOut(): Promise<void> {
    await this.authService.signOut();
    this.router.navigateByUrl('/auth/sign-in', { replaceUrl: true });
  }

  private async initializeUpdater(): Promise<void> {
    if (!this.swUpdate.isEnabled) {
      return;
    }
    timer(0, time.minute * 5).subscribe({
      next: async () => {
        try {
          const updateFound = await this.swUpdate.checkForUpdate();
          if (updateFound) {
            this.showNewVersionAvailable();
          }
        } catch (error) {
          this.errorReportingService.log('AppComponent.initializeUpdater()', 'check-for-update', error);
        }
      },
    });
    const newVersionInstalled = await firstValueFrom(this.dataStorageService.get<boolean>(DataStorageKey.NewVersionInstalled));
    if (newVersionInstalled) {
      this.toastService.showSuccess('Versión actualizada', 'La aplicación ha sido actualizada exitosamente.');
      this.dataStorageService.clear(DataStorageKey.NewVersionInstalled);
    }
  }

  private setDashboardRoutes(): void {
    const session = this.session();
    if (!session) {
      this.dashboardRoutes.set([]);
      return;
    }
    if (session.userPlatformRole === 'admin') {
      this.dashboardRoutes.set([
        { icon: 'home', label: 'Inicio', path: '/dashboard/home' },
        { icon: 'clients', label: 'Admin usuarios', path: '/dashboard/admin/users' },
        { icon: 'briefcase', label: 'Admin empresas', path: '/dashboard/admin/companies' },
        { icon: 'clients', label: 'Admin clientes', path: '/dashboard/admin/clients' },
        { icon: 'file-text', label: 'Admin cotizaciones', path: '/dashboard/admin/quotes' },
        { icon: 'dollar-sign', label: 'Admin pagos', path: '/dashboard/admin/payments' },
        { icon: 'clients', label: 'Clientes', path: '/dashboard/clients' },
        { icon: 'file-text', label: 'Cotizaciones', path: '/dashboard/quotes' },
        { icon: 'dollar-sign', label: 'Pagos', path: '/dashboard/payments' },
      ]);
      return;
    }
    const routes: DashboardRoute[] = [{ icon: 'home', label: 'Inicio', path: '/dashboard/home' }];
    for (const module of session.modules) {
      routes.push(availableDashboardRoutes[module]);
    }
    this.dashboardRoutes.set(routes);
  }

  private showNewVersionAvailable(): void {
    this.toastController.create({
      buttons: [
        {
          handler: async () => {
            const loading = await this.loadingController.create({
              duration: time.second * 2,
              message: 'Actualizando plataforma...',
            });
            await loading.present();
            loading.onWillDismiss().then(() => {
              this.dataStorageService.set(DataStorageKey.NewVersionInstalled, true);
              window.location.reload();
            });
          },
          role: 'confirm',
          text: 'Actualizar',
        },
      ],
      cssClass: 'new-version-available-toast',
      message: 'Nueva versión disponible. Por favor, actualiza la aplicación para continuar.',
    }).then((toast) => toast.present());
  }

  private onViewportChange(matches: boolean): void {
    this.menuSide.set(matches ? 'start' : 'end');
    if (!matches) {
      this.minimize.set(false);
    }
  }
}
